(() => {
  const cleanups = new WeakMap();
  const registrationKey = Symbol.for('theman008.waitlist.registrations');
  const registrations = window[registrationKey] || (window[registrationKey] = new Map());
  const reportTrackingReadiness = (root) => {
    try {
      const privacy = window.Shopify?.customerPrivacy;
      const permission = (method) => {
        try {
          if (typeof privacy?.[method] !== 'function') return 'unavailable';
          const allowed = privacy[method]();
          return typeof allowed === 'boolean' ? String(allowed) : 'unavailable';
        } catch { return 'error'; }
      };
      root.dataset.metaPixelCallable = String(typeof window.fbq === 'function');
      root.dataset.metaPrivacyAvailable = String(Boolean(privacy));
      root.dataset.metaMarketingMethod = String(typeof privacy?.marketingAllowed === 'function');
      root.dataset.metaSaleMethod = String(typeof privacy?.saleOfDataAllowed === 'function');
      root.dataset.metaMarketingAllowed = permission('marketingAllowed');
      root.dataset.metaSaleAllowed = permission('saleOfDataAllowed');
      root.dataset.metaReadiness = root.dataset.metaRegistrationEnabled !== 'true' ? 'disabled'
        : !/^\d{5,25}$/.test(root.dataset.metaPixelId?.trim() || '') ? 'invalid-pixel-id'
        : root.dataset.metaPixelCallable !== 'true' ? 'pixel-unavailable'
        : root.dataset.metaMarketingAllowed !== 'true' || root.dataset.metaSaleAllowed !== 'true' ? 'privacy-not-allowed'
        : 'ready';
    } catch {}
  };
  const privacyLoads = new WeakMap();
  const loadPrivacy = (root, signal) => {
    if (privacyLoads.has(root)) return privacyLoads.get(root);
    const task = new Promise((resolve) => {
      let timer;
      let settled = false;
      let loaderRequested = false;
      const deadline = Date.now() + 2000;
      const ready = () => {
        try {
          return typeof window.Shopify?.customerPrivacy?.marketingAllowed === 'function'
            && typeof window.Shopify?.customerPrivacy?.saleOfDataAllowed === 'function';
        } catch { return false; }
      };
      const finish = (available) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        document.removeEventListener('consentTrackingApiLoaded', check);
        signal?.removeEventListener('abort', cancel);
        reportTrackingReadiness(root);
        root.dataset.metaPrivacyLoadState = available ? 'ready' : 'unavailable';
        resolve(available);
      };
      const cancel = () => finish(false);
      const check = () => {
        if (settled) return;
        if (signal?.aborted) { finish(false); return; }
        if (ready()) { finish(true); return; }
        if (Date.now() >= deadline) { finish(false); return; }
        if (!loaderRequested && typeof window.Shopify?.loadFeatures === 'function') {
          loaderRequested = true;
          try {
            window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.2' }], check);
          } catch { finish(false); return; }
          if (settled) return;
        }
        clearTimeout(timer);
        timer = setTimeout(check, 50);
      };
      reportTrackingReadiness(root);
      root.dataset.metaPrivacyLoadState = 'loading';
      document.addEventListener('consentTrackingApiLoaded', check);
      signal?.addEventListener('abort', cancel, { once: true });
      try { check(); } catch { finish(false); }
    });
    privacyLoads.set(root, task);
    task.then(() => { if (privacyLoads.get(root) === task) privacyLoads.delete(root); });
    return task;
  };
  const recordRegistration = async (root, form, email, isDisposed, signal) => {
    try {
      root.dataset.metaAttemptState = 'accepted';
      const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(email.trim().toLowerCase()));
      const fingerprint = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
      if (isDisposed()) { root.dataset.metaAttemptState = 'disposed'; return; }
      if (registrations.has(fingerprint)) { root.dataset.metaAttemptState = 'duplicate'; return; }
      const eventID = window.crypto.randomUUID();
      registrations.set(fingerprint, eventID);
      if (root.dataset.metaRegistrationEnabled !== 'true') { root.dataset.metaAttemptState = 'disabled'; return; }
      if (!await loadPrivacy(root, signal) || isDisposed()) { root.dataset.metaAttemptState = isDisposed() ? 'disposed' : 'privacy-unavailable'; return; }
      reportTrackingReadiness(root);
      const pixelID = root.dataset.metaPixelId?.trim();
      const privacy = window.Shopify?.customerPrivacy;
      if (!/^\d{5,25}$/.test(pixelID || '') || typeof window.fbq !== 'function') { root.dataset.metaAttemptState = 'pixel-unavailable'; return; }
      if (privacy?.marketingAllowed?.() !== true || privacy?.saleOfDataAllowed?.() !== true || isDisposed()) { root.dataset.metaAttemptState = isDisposed() ? 'disposed' : 'privacy-denied'; return; }
      window.fbq('trackSingle', pixelID, 'CompleteRegistration', {
        content_name: 'After Hours waitlist',
        placement: form.dataset.placement === 'bottom' ? 'bottom' : 'hero',
        registration_stage: 'form_submission'
      }, { eventID });
      root.dataset.metaAttemptState = 'sent';
    } catch { root.dataset.metaAttemptState = 'tracking-error'; }
  };
  const initialize = (scope) => {
    const roots = scope.matches?.('[data-ah-waitlist]') ? [scope] : scope.querySelectorAll('[data-ah-waitlist]');
    roots.forEach((root) => {
      if (cleanups.has(root)) return;
      const controller = new AbortController();
      void loadPrivacy(root, controller.signal);
      const listen = (node, type, handler, options = {}) => node.addEventListener(type, handler, { ...options, signal: controller.signal });
      const desktop = window.matchMedia('(min-width: 1100px)');
      const groups = root.querySelectorAll('.scent-disclosure');
      const disclosures = () => groups.forEach((item) => {
        item.open = desktop.matches;
        const summary = item.querySelector('summary');
        if (desktop.matches) { summary.setAttribute('role', 'heading'); summary.setAttribute('aria-level', '4'); summary.setAttribute('tabindex', '-1'); }
        else { summary.removeAttribute('role'); summary.removeAttribute('aria-level'); summary.removeAttribute('tabindex'); }
      });
      groups.forEach((item) => {
        listen(item.querySelector('summary'), 'click', (event) => { if (desktop.matches) event.preventDefault(); });
        listen(item.querySelector('summary'), 'keydown', (event) => { if (desktop.matches && (event.key === 'Enter' || event.key === ' ')) event.preventDefault(); });
        listen(item, 'toggle', () => { if (desktop.matches && !item.open) item.open = true; });
      });
      disclosures();
      desktop.addEventListener('change', disclosures);
      const offerDetails = root.querySelector('#offer-details');
      const openOfferDetails = () => {
        if (offerDetails && window.location.hash === '#offer-details') {
          offerDetails.open = true;
          offerDetails.scrollIntoView({ block: 'start' });
        }
      };
      listen(window, 'hashchange', openOfferDetails);
      openOfferDetails();
      const countdown = root.querySelector('[data-countdown]');
      const deadline = Date.parse(countdown?.dataset.deadline);
      let timer;
      if (countdown && Number.isFinite(deadline)) {
        const ended = root.querySelector('[data-countdown-ended]');
        const tick = () => {
          const remaining = Math.max(0, deadline - Date.now());
          countdown.hidden = remaining === 0;
          ended.hidden = remaining !== 0;
          countdown.dataset.state = remaining === 0 ? 'ended' : 'active';
          const seconds = Math.floor(remaining / 1000);
          const values = { days: Math.floor(seconds / 86400), hours: Math.floor(seconds / 3600) % 24, minutes: Math.floor(seconds / 60) % 60, seconds: seconds % 60 };
          Object.entries(values).forEach(([key, value]) => { countdown.querySelector(`[data-count="${key}"]`).textContent = String(value).padStart(2, '0'); });
        };
        tick();
        timer = setInterval(tick, 1000);
      }
      const forms = [...root.querySelectorAll('[data-ah-signup]')];
      const site = root.dataset.siteId?.trim();
      const list = root.dataset.listId?.trim();
      const ready = root.dataset.captureEnabled === 'true' && /^[A-Za-z0-9]{6}$/.test(site || '') && /^[A-Za-z0-9]{6}$/.test(list || '');
      let pending = null;
      let disposed = false;
      let accepted = false;
      const setAvailable = (available) => forms.forEach((form) => { form.querySelector('[data-signup-fields]').disabled = !available; });
      forms.forEach((form) => {
        const input = form.querySelector('[data-signup-email]');
        const feedback = form.querySelector('[data-signup-feedback]');
        const panel = form.querySelector('[data-signup-accepted]');
        feedback.classList.remove('visually-hidden');
        form.querySelector('[data-signup-fields]').hidden = false;
        panel.hidden = true;
        listen(form.querySelector('[data-signup-reset]'), 'click', () => {
          if (pending || disposed) return;
          accepted = false;
          forms.forEach((item) => {
            item.reset();
            item.querySelector('[data-signup-fields]').hidden = false;
            item.querySelector('[data-signup-accepted]').hidden = true;
            item.querySelector('[data-signup-feedback]').textContent = '';
            item.querySelector('[data-signup-feedback]').classList.remove('is-error', 'visually-hidden');
            item.querySelector('[data-signup-email]').removeAttribute('aria-invalid');
          });
          setAvailable(ready);
          input.focus();
        });
        const label = form.querySelector('[data-submit-label]');
        const idleLabel = form.dataset.idleLabel || label.textContent;
        form.dataset.idleLabel = idleLabel;
        label.textContent = idleLabel;
        form.removeAttribute('aria-busy');
        if (ready) feedback.textContent = '';
        else feedback.textContent = root.dataset.fallbackMessage || feedback.textContent;
        listen(input, 'input', () => { input.removeAttribute('aria-invalid'); feedback.classList.remove('is-error'); feedback.textContent = ''; });
        listen(form, 'submit', async (event) => {
          event.preventDefault();
          if (!ready || pending || accepted || disposed) return;
          if (!input.checkValidity()) {
            input.setAttribute('aria-invalid', 'true');
            feedback.classList.add('is-error');
            feedback.textContent = input.validity.valueMissing ? root.dataset.emptyMessage : root.dataset.invalidMessage;
            input.reportValidity();
            return;
          }
          input.removeAttribute('aria-invalid');
          feedback.classList.remove('is-error');
          const submittedEmail = input.value.trim();
          const request = new AbortController();
          pending = request;
          setAvailable(false);
          form.setAttribute('aria-busy', 'true');
          label.textContent = root.dataset.submittingLabel;
          feedback.textContent = root.dataset.submittingMessage;
          let timedOut = false;
          const timeout = setTimeout(() => { timedOut = true; request.abort(); }, 12000);
          try {
            const response = await fetch('https://a.klaviyo.com/client/subscriptions?company_id=' + encodeURIComponent(site), {
              method: 'POST', mode: 'cors', credentials: 'omit', referrerPolicy: 'no-referrer',
              headers: { 'Content-Type': 'application/vnd.api+json', Accept: 'application/vnd.api+json', revision: '2026-10-15' },
              signal: request.signal,
              body: JSON.stringify({ data: { type: 'subscription', attributes: { custom_source: 'After Hours waitlist — ' + form.dataset.placement, profile: { data: { type: 'profile', attributes: { email: input.value.trim(), subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } } } } }, relationships: { list: { data: { type: 'list', id: list } } } } })
            });
            if (disposed) return;
            if (response.status === 202) {
              accepted = true;
              forms.forEach((item) => {
                item.querySelector('[data-signup-fields]').hidden = true;
                item.querySelector('[data-signup-accepted]').hidden = false;
                item.querySelector('[data-signup-feedback]').classList.remove('is-error');
                item.querySelector('[data-signup-feedback]').textContent = '';
              });
              feedback.classList.add('visually-hidden');
              feedback.textContent = panel.querySelector('.accepted-status').textContent;
              panel.focus({ preventScroll: true });
              panel.scrollIntoView({ block: 'center', behavior: 'instant' });
              void recordRegistration(root, form, submittedEmail, () => disposed, controller.signal);
            } else {
              feedback.classList.add('is-error');
              feedback.textContent = response.status === 429 ? root.dataset.rateMessage : root.dataset.errorMessage;
            }
          } catch {
            if (disposed) return;
            feedback.classList.add('is-error');
            feedback.textContent = timedOut ? root.dataset.timeoutMessage : root.dataset.errorMessage;
          } finally {
            clearTimeout(timeout);
            if (pending === request) pending = null;
            if (!disposed) { setAvailable(ready && !accepted); form.removeAttribute('aria-busy'); label.textContent = idleLabel; }
          }
        });
      });
      setAvailable(ready);
      const email = root.querySelector('[data-hero-email]');
      listen(root.querySelector('.header-cta'), 'click', (event) => {
        event.preventDefault();
        if (accepted) {
          const panel = forms[0].querySelector('[data-signup-accepted]');
          panel.focus({ preventScroll: true });
          panel.scrollIntoView({ block: 'center', behavior: 'instant' });
          return;
        }
        if (!ready || pending) { root.querySelector('[data-signup-feedback]').scrollIntoView({ block: 'center' }); return; }
        email.focus({ preventScroll: true });
        email.scrollIntoView({ block: 'center', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      });
      cleanups.set(root, () => { disposed = true; pending?.abort(); setAvailable(false); clearInterval(timer); controller.abort(); desktop.removeEventListener('change', disclosures); });
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true });
  else initialize(document);
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    (event.target.matches?.('[data-ah-waitlist]') ? [event.target] : event.target.querySelectorAll('[data-ah-waitlist]')).forEach((root) => { cleanups.get(root)?.(); cleanups.delete(root); });
  });
})();
